import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

// Must match the Mongoose `maxlength` on Announcement.message and the
// Joi cap in middleware/validation.js. The HTML markup from the rich-text
// editor counts toward this limit.
const MAX_MESSAGE_LENGTH = 20000;

const TRANSACTIONAL_EMAILS = [
  { id: 'enrollment_confirmation', name: 'Enrollment Confirmation', description: 'Sent when a student successfully enrolls in a course', recipient: 'Student' },
  { id: 'payment_receipt', name: 'Payment Receipt', description: 'Sent after each successful payment', recipient: 'Student' },
  { id: 'payment_failed', name: 'Payment Failed', description: 'Sent when a payment is declined or fails', recipient: 'Student' },
  { id: 'account_created', name: 'Account Created', description: 'Welcome email sent when a student account is created', recipient: 'Student' }
];

const LIFECYCLE_EMAILS = [
  { id: 'class_reminder', name: 'Class Reminder', description: 'Sent 1 day before a scheduled class', recipient: 'Student' },
  { id: 'material_released', name: 'Material Released', description: 'Sent when new class materials are unlocked', recipient: 'Student' },
  { id: 'low_attendance', name: 'Low Attendance Alert', description: "Sent when a student's attendance drops below 70%", recipient: 'Student' },
  { id: 'level_promotion', name: 'Level Promotion', description: 'Sent when a student is promoted to the next level', recipient: 'Student' },
  { id: 'pending_enrollment_alert', name: 'Pending Sign-up Alert', description: 'Sent the moment a student starts checkout for your course (payment may still be pending)', recipient: 'Teacher' },
  { id: 'new_enrollment_alert', name: 'New Enrollment Confirmed', description: "Sent when a student's payment clears and they're fully enrolled", recipient: 'Teacher' },
  { id: 'assessment_reminder', name: 'Assessment Reminders', description: 'Sent 1 week and 1 day before each assessment', recipient: 'Student' },
  { id: 'assessment_postponed', name: 'Assessment Rescheduled', description: "Sent when an assessment's date is changed", recipient: 'Student' },
  { id: 'payment_receipt', name: 'Payment Receipt', description: 'Sent after each successful subscription renewal or one-time charge', recipient: 'Student' },
  { id: 'class_cancelled', name: 'Class Cancelled', description: 'Sent when a teacher cancels a class for a specific date', recipient: 'Student' },
  { id: 'pending_payment_reminder', name: 'Pending Payment Reminder', description: 'Sent automatically the day after someone starts enrolling but doesn\'t finish payment', recipient: 'Student' }
];

const WHATSAPP_MESSAGES = [
  { id: 'class_reminder_wa', name: 'Class Reminder', description: 'Sent 24 hours before a scheduled class', recipient: 'Student' },
  { id: 'enrollment_wa', name: 'Enrollment Confirmation', description: 'Sent when a student enrolls', recipient: 'Student' },
  { id: 'material_released_wa', name: 'Material Released', description: 'Sent when new materials are available', recipient: 'Student' },
];

const ANNOUNCEMENT_TEMPLATES = [
  {
    id: 'class_cancelled',
    name: 'Class Cancelled',
    subject: 'Class cancelled — [Class Name]',
    message: 'Dear students,\n\nUnfortunately, today\'s class has been cancelled. We apologize for the inconvenience.\n\nRegular classes will resume as scheduled. Please contact your teacher for more information.\n\nThank you for your understanding.'
  },
  {
    id: 'class_online',
    name: 'Class Moved Online',
    subject: 'Today\'s class moved online — [Class Name]',
    message: 'Dear students,\n\nToday\'s class will be held online instead of in person.\n\nPlease contact your teacher for the meeting link. Class timing remains the same.\n\nWe look forward to seeing you online!'
  },
  {
    id: 'schedule_change',
    name: 'Schedule Change',
    subject: 'Schedule update — [Class Name]',
    message: 'Dear students,\n\nPlease note the following schedule change for your class:\n\nPrevious: [old day/time]\nNew: [new day/time]\nEffective from: [date]\n\nPlease adjust your calendars accordingly. If you have any questions, feel free to reach out.'
  },
  {
    id: 'festival_holiday',
    name: 'Festival / Holiday Notice',
    subject: 'Holiday notice — [Festival Name]',
    message: 'Dear students,\n\nWishing you and your families a joyous [Festival Name]!\n\nPlease note that classes will be closed on [dates]. Regular classes will resume on [date].\n\nMay this festival bring you happiness and inspiration in your dance journey.'
  },
  {
    id: 'arangetram',
    name: 'Arangetram Announcement',
    subject: 'Arangetram announcement — [Student Name]',
    message: 'Dear students and families,\n\nWe are delighted to announce that [Student Name] will be performing their Arangetram (solo debut recital).\n\nDate: [date]\nTime: [time]\nVenue: [location]\n\nThis milestone represents years of dedicated practice and devotion to the art form. Your presence and blessings would mean the world.\n\nPlease RSVP by [date].'
  },
  {
    id: 'annual_day',
    name: 'Annual Day / Recital',
    subject: 'Annual Day performance — Save the date!',
    message: 'Dear students and families,\n\nWe are excited to announce our Annual Day celebration!\n\nDate: [date]\nTime: [time]\nVenue: [location]\n\nAll students are expected to participate. Rehearsal schedule will be shared shortly. Please ensure regular attendance in the weeks leading up to the event.\n\nCostume and stage details will follow.'
  },
  {
    id: 'new_batch',
    name: 'New Batch Starting',
    subject: 'New [Level/Style] batch starting — Enroll now!',
    message: 'Dear students and families,\n\nWe are starting a new batch for [Level/Style]!\n\nStarting: [date]\nSchedule: [day(s)] at [time]\nSuitable for: [age group / experience level]\n\nThis is a wonderful opportunity for those looking to begin or continue their journey in [dance form].\n\nSeats are limited. To enroll, please visit your dashboard or contact us directly.'
  },
  {
    id: 'assessment_notice',
    name: 'Assessment / Exam Notice',
    subject: 'Upcoming assessment — [Class Name]',
    message: 'Dear students,\n\nA reminder that your assessment is approaching:\n\nDate: [date]\nTime: [time]\nLocation: [venue]\n\nPlease prepare the following items:\n- [Syllabus/topics covered]\n- [Required attire]\n- [Any materials to bring]\n\nRegular practice is key. If you have any questions about the assessment format, please ask during your next class.'
  },
  {
    id: 'practice_reminder',
    name: 'Practice Reminder',
    subject: 'Practice reminder — Keep up the momentum!',
    message: 'Dear students,\n\nA gentle reminder to keep up your daily practice routine. Even 15-20 minutes of focused practice each day makes a significant difference.\n\nFocus areas this week:\n- [Item 1]\n- [Item 2]\n- [Item 3]\n\nRemember — consistent practice builds muscle memory and confidence. See you in class!'
  },
  {
    id: 'workshop',
    name: 'Workshop / Masterclass',
    subject: 'Special workshop — [Topic / Guest Artist]',
    message: 'Dear students and families,\n\nWe are thrilled to announce a special workshop:\n\nTopic: [workshop topic]\nInstructor: [name and credentials]\nDate: [date]\nTime: [time]\nVenue: [location]\nFee: [amount or "Included with enrollment"]\n\nThis is a unique opportunity to deepen your knowledge and learn from an accomplished artist. Open to [who can attend].\n\nSpaces are limited — register early!'
  },
  {
    id: 'costume_update',
    name: 'Costume / Uniform Update',
    subject: 'Costume details — [Event / Class Name]',
    message: 'Dear students and families,\n\nPlease find the costume/uniform details below:\n\nFor: [event or regular class]\nRequired attire: [description]\nWhere to purchase: [vendor/link]\nDeadline: [date]\n\nIf you need help with sizing or have any questions, please reach out before the deadline.\n\nThank you!'
  },
  {
    id: 'fee_revision',
    name: 'Fee Revision Notice',
    subject: 'Tuition update — Effective [date]',
    message: 'Dear students and families,\n\nWe would like to inform you of an update to our tuition fees, effective [date].\n\nNew monthly fee: $[amount]\nPrevious fee: $[amount]\n\nThis adjustment allows us to continue providing high-quality instruction and maintain our facilities. Current students enrolled before [date] will receive [any grandfathering details].\n\nIf you have any questions or concerns, please don\'t hesitate to reach out. We truly value each student and are committed to making this journey meaningful.'
  }
];

const CustomSelect = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = options.find(o => o.value === value);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={ref} className="relative flex-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full border border-gray-300 rounded-lg px-4 py-2 text-left text-sm bg-white focus:ring-2 focus:ring-brand-400 focus:border-transparent flex items-center justify-between"
      >
        <span className={selected ? 'text-gray-900' : 'text-gray-400'}>{selected?.label || placeholder}</span>
        <svg className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              onClick={() => { onChange(opt.value); setOpen(false); }}
              className={`w-full px-4 py-2.5 text-left text-sm flex items-center justify-between transition-colors ${
                opt.value === value
                  ? 'bg-brand-50 text-brand-600 font-medium'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              <span>{opt.label}</span>
              {opt.value === value && (
                <svg className="w-4 h-4 text-brand-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const editorStyle = `
  [contenteditable]:empty:before {
    content: attr(data-placeholder);
    color: #9ca3af;
    pointer-events: none;
  }
  [contenteditable] a { color: #3F51B5; text-decoration: underline; }
  [contenteditable] ul { padding-left: 1.5em; margin: 0.5em 0; }
  [contenteditable] li { margin-bottom: 0.25em; }
  [contenteditable] img { max-width: 100%; height: auto; border-radius: 8px; margin: 8px 0; }
`;

const NotificationsSettings = () => {
  const { studio, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('email');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Announcement state
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [recipientCount, setRecipientCount] = useState(null);
  const [annSubject, setAnnSubject] = useState('');
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [annSuccess, setAnnSuccess] = useState('');
  const [annError, setAnnError] = useState('');
  const [sentHistory, setSentHistory] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);

  const updateMessageLength = useCallback(() => {
    setMessageLength(editorRef.current?.innerHTML.length || 0);
  }, []);

  const [lifecycle, setLifecycle] = useState({
    class_reminder: true, material_released: true,
    low_attendance: true, level_promotion: true, new_enrollment_alert: true, pending_enrollment_alert: true,
    assessment_reminder: true, assessment_postponed: true,
    payment_receipt: true, class_cancelled: true,
    pending_payment_reminder: true
  });

  useEffect(() => {
    if (studio?.emailSettings?.lifecycle) {
      setLifecycle(prev => ({ ...prev, ...studio.emailSettings.lifecycle }));
    }
  }, [studio]);

  // Fetch courses + sent history on mount
  useEffect(() => {
    api.get('/courses').then(({ data }) => setCourses((data.courses || data || []).filter(c => c.status === 'active')));
    api.get('/announcements').then(({ data }) => setSentHistory(data.announcements || [])).catch(() => {});
  }, []);

  // Fetch recipient count when course selection or filter changes
  useEffect(() => {
    if (!selectedCourse) { setRecipientCount(null); return; }
    const params = includeInactive ? '?include=all' : '';
    api.get(`/announcements/recipients/${selectedCourse}${params}`)
      .then(({ data }) => setRecipientCount(data.count))
      .catch(() => setRecipientCount(null));
  }, [selectedCourse, includeInactive]);

  const toggleLifecycle = (key) => {
    setLifecycle(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSave = async () => {
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put('/studio', { emailSettings: { lifecycle } });
      await refreshUser();
      setSuccess('Settings saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings');
    } finally { setSaving(false); }
  };

  const execFormat = useCallback((command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
  }, []);

  const handleInsertLink = useCallback(() => {
    const url = prompt('Enter URL:');
    if (url) execFormat('createLink', url);
  }, [execFormat]);

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setAnnError('Image must be under 2MB');
      return;
    }
    setUploading(true); setAnnError('');
    try {
      const formData = new FormData();
      formData.append('image', file);
      const { data } = await api.post('/announcements/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      editorRef.current?.focus();
      document.execCommand('insertImage', false, data.imageUrl);
      updateMessageLength();
    } catch (err) {
      setAnnError(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  }, [updateMessageLength]);

  const handleApplyTemplate = useCallback((templateId) => {
    if (!templateId) return;
    const tpl = ANNOUNCEMENT_TEMPLATES.find(t => t.id === templateId);
    if (!tpl) return;
    setAnnSubject(tpl.subject);
    if (editorRef.current) {
      editorRef.current.innerHTML = tpl.message.replace(/\n/g, '<br>');
      updateMessageLength();
    }
  }, [updateMessageLength]);

  const getEditorContent = () => editorRef.current?.innerHTML || '';
  const getEditorText = () => editorRef.current?.innerText || '';

  const handleSendAnnouncement = async () => {
    setSending(true); setAnnError('');
    try {
      const message = getEditorContent();
      const { data } = await api.post('/announcements', {
        courseId: selectedCourse,
        subject: annSubject,
        message,
        includeInactive
      });
      setAnnSuccess(`Announcement sent to ${data.recipientCount} student(s)!`);
      setAnnSubject(''); setSelectedCourse(''); setShowConfirm(false);
      setRecipientCount(null);
      if (editorRef.current) editorRef.current.innerHTML = '';
      updateMessageLength();
      api.get('/announcements').then(({ data }) => setSentHistory(data.announcements || [])).catch(() => {});
      setTimeout(() => setAnnSuccess(''), 5000);
    } catch (err) {
      setAnnError(err.response?.data?.error || 'Failed to send announcement');
      setShowConfirm(false);
    } finally { setSending(false); }
  };

  return (
    <div>
      <style>{editorStyle}</style>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Notifications</h1>
      <p className="text-sm text-gray-500 mb-4">Configure how and when notifications are sent to your students.</p>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab('email')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2 ${
            activeTab === 'email' ? 'text-brand-500 border-brand-500' : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />
          </svg>
          Email
        </button>
        <button
          onClick={() => setActiveTab('whatsapp')}
          className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px flex items-center gap-2 ${
            activeTab === 'whatsapp' ? 'text-green-600 border-green-500' : 'text-gray-500 border-transparent hover:text-gray-700'
          }`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
          </svg>
          WhatsApp
        </button>
      </div>

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* ─── Email Tab ──────────────────────────── */}
      {activeTab === 'email' && (
        <>
          {/* Announcements & Newsletters */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Announcements & Newsletters</h2>
            <p className="text-sm text-gray-500 mb-4">Send emails to your students — class updates, schedule changes, or studio newsletters.</p>

            {annSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">{annSuccess}</div>
            )}
            {annError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{annError}</div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Send to</label>
                <div className="flex items-center gap-3">
                  <CustomSelect
                    value={selectedCourse}
                    onChange={setSelectedCourse}
                    placeholder="Select audience..."
                    options={[
                      { value: 'all', label: 'All Students (studio-wide)' },
                      ...courses.map(c => ({ value: c._id, label: c.name }))
                    ]}
                  />
                  {recipientCount !== null && (
                    <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-brand-50 text-brand-600 whitespace-nowrap">
                      {recipientCount} student{recipientCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                {selectedCourse && (
                  <label className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      checked={includeInactive}
                      onChange={e => setIncludeInactive(e.target.checked)}
                      className="rounded border-gray-300 text-brand-500 focus:ring-brand-400"
                    />
                    <span className="text-xs text-gray-500">Include inactive/past students</span>
                  </label>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                <CustomSelect
                  value=""
                  onChange={handleApplyTemplate}
                  placeholder="Start from scratch or choose a template..."
                  options={ANNOUNCEMENT_TEMPLATES.map(t => ({ value: t.id, label: t.name }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={annSubject}
                  onChange={e => setAnnSubject(e.target.value)}
                  placeholder="Enter subject"
                  maxLength={200}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-brand-400 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                {/* Formatting Toolbar */}
                <div className="flex items-center gap-1 border border-gray-300 border-b-0 rounded-t-lg px-2 py-1.5 bg-gray-50">
                  <button
                    type="button"
                    onClick={() => execFormat('bold')}
                    className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                    title="Bold"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => execFormat('italic')}
                    className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                    title="Italic"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 4h4l-4 16H6" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => execFormat('underline')}
                    className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                    title="Underline"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 4v8a4 4 0 0 0 8 0V4M6 20h12" />
                    </svg>
                  </button>
                  <div className="w-px h-5 bg-gray-300 mx-1" />
                  <button
                    type="button"
                    onClick={handleInsertLink}
                    className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                    title="Insert Link"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244" />
                    </svg>
                  </button>
                  <div className="w-px h-5 bg-gray-300 mx-1" />
                  <button
                    type="button"
                    onClick={() => execFormat('insertUnorderedList')}
                    className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors"
                    title="Bullet List"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h8.25M8.25 12h8.25m-8.25 5.25h8.25M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                    </svg>
                  </button>
                  <div className="w-px h-5 bg-gray-300 mx-1" />
                  <button
                    type="button"
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploading}
                    className="p-1.5 rounded hover:bg-gray-200 text-gray-600 transition-colors disabled:opacity-50"
                    title="Upload Image (max 2MB)"
                  >
                    {uploading ? (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
                      </svg>
                    )}
                  </button>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </div>
                {/* Rich Text Editor */}
                <div
                  ref={editorRef}
                  contentEditable
                  className="w-full border border-gray-300 rounded-b-lg px-4 py-3 min-h-[180px] max-h-[400px] overflow-y-auto focus:ring-2 focus:ring-brand-400 focus:border-transparent focus:outline-none text-sm text-gray-800 leading-relaxed"
                  style={{ whiteSpace: 'pre-wrap' }}
                  data-placeholder="Write your announcement or newsletter..."
                  onFocus={e => { if (e.target.innerText.trim() === '') e.target.classList.remove('empty'); }}
                  onInput={updateMessageLength}
                />
                <div className="flex justify-between items-start mt-1.5 gap-3">
                  <p className="text-xs text-gray-400">
                    Images: PNG, JPG, or GIF — max 2MB each. Recommended width 600px for best email display. Use landscape or square images for best results.
                  </p>
                  <p className={`text-xs whitespace-nowrap flex-shrink-0 ${
                    messageLength > MAX_MESSAGE_LENGTH ? 'text-red-600 font-medium' :
                    messageLength > MAX_MESSAGE_LENGTH * 0.9 ? 'text-amber-600' :
                    'text-gray-400'
                  }`}>
                    {messageLength.toLocaleString()} / {MAX_MESSAGE_LENGTH.toLocaleString()} characters
                    {messageLength > MAX_MESSAGE_LENGTH && (
                      <span className="block text-right">Too long — trim {(messageLength - MAX_MESSAGE_LENGTH).toLocaleString()} char{messageLength - MAX_MESSAGE_LENGTH === 1 ? '' : 's'}</span>
                    )}
                  </p>
                </div>
              </div>

              {!showConfirm ? (
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowConfirm(true)}
                    disabled={!selectedCourse || !annSubject.trim() || !getEditorText().trim() || sending || messageLength > MAX_MESSAGE_LENGTH}
                    className="bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    Send
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    Send to <strong>{recipientCount || '?'} student{recipientCount !== 1 ? 's' : ''}</strong> in{' '}
                    <strong>{selectedCourse === 'all' ? 'all courses' : courses.find(c => c._id === selectedCourse)?.name || 'selected course'}</strong>?
                  </p>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => setShowConfirm(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSendAnnouncement}
                      disabled={sending}
                      className="bg-brand-500 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors"
                    >
                      {sending ? 'Sending...' : 'Confirm Send'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sent History */}
          {sentHistory.length > 0 && (
            <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-1">Sent History</h2>
                  <p className="text-sm text-gray-500">Recent emails sent from your studio.</p>
                </div>
                {sentHistory[0] && (
                  <span className="text-xs text-gray-400">
                    Last sent {new Date(sentHistory[0].sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                )}
              </div>
              <div className="divide-y divide-gray-50">
                {sentHistory.slice(0, 10).map(ann => (
                  <div key={ann._id} className="py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{ann.subject}</p>
                      <p className="text-xs text-gray-500">
                        {ann.course?.name || 'All Students'} &middot; {ann.recipientCount} student{ann.recipientCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {new Date(ann.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Transactional Emails */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold text-gray-900">Transactional Emails</h2>
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Always On</span>
            </div>
            <p className="text-sm text-gray-500 mb-4">Required for your studio to function. Cannot be disabled.</p>
            <div className="divide-y divide-gray-50">
              {TRANSACTIONAL_EMAILS.map(email => (
                <div key={email.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{email.name}</p>
                    <p className="text-xs text-gray-500">{email.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{email.recipient}</span>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium">Active</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Lifecycle Emails */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Lifecycle Emails</h2>
            <p className="text-sm text-gray-500 mb-4">Automated emails triggered by student activity.</p>
            <div className="divide-y divide-gray-50">
              {LIFECYCLE_EMAILS.map(email => (
                <div key={email.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{email.name}</p>
                    <p className="text-xs text-gray-500">{email.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${email.recipient === 'Teacher' ? 'bg-purple-50 text-purple-600' : 'bg-blue-50 text-blue-600'}`}>{email.recipient}</span>
                    <button
                      onClick={() => toggleLifecycle(email.id)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${lifecycle[email.id] ? 'bg-brand-500' : 'bg-gray-200'}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${lifecycle[email.id] ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={handleSave} disabled={saving} className="bg-brand-500 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-brand-600 disabled:bg-gray-300 transition-colors">
              {saving ? 'Saving...' : 'Save Email Settings'}
            </button>
          </div>
        </>
      )}

      {/* ─── WhatsApp Tab ───────────────────────── */}
      {activeTab === 'whatsapp' && (
        <>
          {/* Connection Status */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold text-gray-900">WhatsApp Business</h2>
              {studio?.whatsapp?.connected ? (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">Connected</span>
              ) : (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Not Connected</span>
              )}
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Connect your WhatsApp Business account to send class reminders and notifications directly to students.
            </p>

            {studio?.whatsapp?.connected ? (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-green-500" />
                  <span className="text-sm font-medium text-green-700">WhatsApp Connected</span>
                </div>
                <div className="text-sm text-gray-500 space-y-1 mb-4">
                  <div>Phone: {studio.whatsapp.phoneNumber || '—'}</div>
                  {studio.whatsapp.connectedAt && <div>Connected {new Date(studio.whatsapp.connectedAt).toLocaleDateString()}</div>}
                </div>
                <button className="text-sm text-red-500 hover:text-red-700 font-medium">
                  Disconnect WhatsApp
                </button>
              </div>
            ) : (
              <div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <svg className="w-6 h-6 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 mb-1">Connect WhatsApp Business</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Send class reminders, enrollment confirmations, and announcements directly to your students on WhatsApp.
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1.5 mb-5">
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                          Automatic class reminders 24h before
                        </li>
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                          Enrollment confirmations
                        </li>
                        <li className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                          New material notifications
                        </li>
                      </ul>
                      <button
                        className="bg-green-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors flex items-center gap-2"
                        onClick={() => {
                          // TODO: Implement Meta Embedded Signup flow
                          // For now, show coming soon
                          alert('WhatsApp Business Embedded Signup coming soon. For now, class reminders are sent via DanceHall\'s WhatsApp number.');
                        }}
                      >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z"/>
                        </svg>
                        Connect WhatsApp Business
                      </button>
                    </div>
                  </div>
                </div>

                {/* Interim: DanceHall WhatsApp */}
                <div className="bg-white p-5 rounded-xl border border-gray-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <span className="text-sm font-medium text-green-700">DanceHall WhatsApp Active</span>
                  </div>
                  <p className="text-sm text-gray-500">
                    While your own WhatsApp isn't connected, class reminders are sent from DanceHall's verified WhatsApp number (+1 551-401-3075).
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* WhatsApp Message Types */}
          <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">WhatsApp Messages</h2>
            <p className="text-sm text-gray-500 mb-4">Messages sent to students via WhatsApp.</p>
            <div className="divide-y divide-gray-50">
              {WHATSAPP_MESSAGES.map(msg => (
                <div key={msg.id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{msg.name}</p>
                    <p className="text-xs text-gray-500">{msg.description}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-xs px-2 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">{msg.recipient}</span>
                    <span className="text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-700 font-medium">Active</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationsSettings;
